/**
 * Folder path utilities for navigating the folder hierarchy
 */

/**
 * Normalizes a folder path to ensure it has a leading and trailing slash.
 * Handles cases where backend may not include trailing slash.
 *
 * @param path - The path to normalize (may be undefined, null, empty, or missing slashes)
 * @returns Normalized path with leading and trailing slashes, or '/' for root
 *
 * @example
 * normalizeFolderPath('Documents') -> '/Documents/'
 * normalizeFolderPath('/Documents') -> '/Documents/'
 * normalizeFolderPath('/Documents/') -> '/Documents/'
 * normalizeFolderPath('') -> '/'
 * normalizeFolderPath(undefined) -> '/'
 */
export function normalizeFolderPath(path: string | undefined | null): string {
  if (path === null || path === undefined || path.trim() === '') {
    return '/';
  }

  let normalized = path.trim();

  // Ensure leading slash
  if (!normalized.startsWith('/')) {
    normalized = '/' + normalized;
  }

  // Ensure trailing slash
  if (!normalized.endsWith('/')) {
    normalized = normalized + '/';
  }

  // Handle double slashes (but not leading //)
  normalized = normalized.replace(/\/+/g, '/');

  return normalized;
}

/**
 * Get parent path from current path.
 *
 * @param currentPath - The current folder path
 * @returns The parent folder path, or '/' if already at root
 *
 * @example
 * getParentPath('/Documents/Legal/') -> '/Documents/'
 * getParentPath('/Documents/') -> '/'
 * getParentPath('/') -> '/'
 */
export function getParentPath(currentPath: string): string {
  const normalized = normalizeFolderPath(currentPath);

  if (normalized === '/') {
    return '/';
  }

  // Remove trailing slash, split, remove last segment, rejoin
  const segments = normalized.slice(0, -1).split('/').filter(Boolean);
  segments.pop();

  if (segments.length === 0) {
    return '/';
  }

  return '/' + segments.join('/') + '/';
}

/**
 * Check if a folder is a direct child of the current path.
 * A direct child has exactly one additional path segment.
 *
 * @param folderPath - The folder path to check
 * @param currentPath - The current path we're viewing
 * @returns true if folderPath is a direct child of currentPath
 *
 * @example
 * isDirectChildFolder('/Documents/', '/') -> true
 * isDirectChildFolder('/Documents/Legal/', '/') -> false (nested)
 * isDirectChildFolder('/Documents/Legal/', '/Documents/') -> true
 * isDirectChildFolder('/Images/', '/Documents/') -> false (different branch)
 */
export function isDirectChildFolder(folderPath: string, currentPath: string): boolean {
  const normalizedFolder = normalizeFolderPath(folderPath);
  const normalizedCurrent = normalizeFolderPath(currentPath);

  // Folder must start with current path
  if (!normalizedFolder.startsWith(normalizedCurrent)) {
    return false;
  }

  // Get the remainder after current path
  const remainder = normalizedFolder.slice(normalizedCurrent.length);

  // Direct child should have exactly one segment (the folder name)
  // e.g., "Documents/" has one segment "Documents"
  const segments = remainder.split('/').filter(Boolean);

  return segments.length === 1;
}

/**
 * Check if a file belongs to the current path.
 * Files have a path indicating their containing folder.
 *
 * @param filePath - The file's path property (the folder it's in)
 * @param currentPath - The current path we're viewing
 * @returns true if the file is directly in currentPath (not in a subfolder)
 *
 * @example
 * isFileInCurrentPath('/', '/') -> true
 * isFileInCurrentPath('/Documents/', '/Documents/') -> true
 * isFileInCurrentPath('/Documents/', '/') -> false (file is in subfolder)
 */
export function isFileInCurrentPath(filePath: string | undefined, currentPath: string): boolean {
  const normalizedFilePath = normalizeFolderPath(filePath);
  const normalizedCurrent = normalizeFolderPath(currentPath);

  return normalizedFilePath === normalizedCurrent;
}

/**
 * Extracts the folder display name from a folder path.
 * Takes the last segment of the path as the display name.
 *
 * @param folderPath - The folder path (e.g., "/test/" or "/Documents/Legal/")
 * @returns The folder display name (e.g., "test" or "Legal")
 *
 * @example
 * getFolderNameFromPath('/test/') -> 'test'
 * getFolderNameFromPath('/Documents/Legal/') -> 'Legal'
 * getFolderNameFromPath('/') -> ''
 * getFolderNameFromPath('') -> ''
 */
export function getFolderNameFromPath(folderPath: string | undefined | null): string {
  if (folderPath === null || folderPath === undefined || folderPath.trim() === '') {
    return '';
  }

  // Remove leading/trailing slashes and split
  const segments = folderPath.trim().split('/').filter(Boolean);

  if (segments.length === 0) {
    return '';
  }

  // Return the last segment as the folder name
  return segments[segments.length - 1];
}
